import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store/authStore';

// Components
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import AppLayout from './components/layout/AppLayout/AppLayout';
import Spinner from './components/ui/Spinner';

// Lazy Loaded Pages
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const VerifyOTP = lazy(() => import('./pages/VerifyOTP'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Payments = lazy(() => import('./pages/Payments'));
const Transactions = lazy(() => import('./pages/Transactions'));
const Settings = lazy(() => import('./pages/Settings'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const Team = lazy(() => import('./pages/Team'));
const Contact = lazy(() => import('./pages/Contact'));

// Admin Pages
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminDeposits = lazy(() => import('./pages/admin/AdminDeposits'));
const AdminWithdrawals = lazy(() => import('./pages/admin/AdminWithdrawals'));
const AdminPayments = lazy(() => import('./pages/admin/AdminPayments'));
const SystemSettingsPage = lazy(() => import('./pages/admin/SystemSettingsPage'));
const AdminBanners = lazy(() => import('./pages/admin/AdminBanners'));

const LoadingFallback = () => (
  <div className="flex justify-center items-center h-screen w-full bg-slate-50">
    <Spinner size="lg" />
  </div>
);

function App() {
  const { isAuthenticated, isAdmin } = useAuthStore();
  const checkAuth = useAuthStore((s) => s.checkAuth);
  const authChecked = useAuthStore((s) => s.authChecked);

  useEffect(() => {
    console.log('App mounting, checkAuth starting...');
    if (typeof checkAuth === 'function') checkAuth();
  }, [checkAuth]);

  return (
    <Router>
      <div className="App">
        {!authChecked && (
          <div className="app-auth-loading">
            <div className="loader">Authenticating...</div>
          </div>
        )}
        <Toaster position="top-center" />
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to={isAdmin() ? "/admin" : "/dashboard"} />} />
            <Route path="/register" element={!isAuthenticated ? <Register /> : <Navigate to={isAdmin() ? "/admin" : "/dashboard"} />} />
            <Route path="/verify-otp" element={!isAuthenticated ? <VerifyOTP /> : <Navigate to={isAdmin() ? "/admin" : "/dashboard"} />} />
            <Route path="/forgot-password" element={!isAuthenticated ? <ForgotPassword /> : <Navigate to={isAdmin() ? "/admin" : "/dashboard"} />} />

            {/* Protected user routes */}
            <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/transactions" element={<Transactions />} />
              <Route path="/payments" element={<Payments />} />

              {/* Redirect legacy routes */}
              <Route path="/deposit" element={<Navigate to="/transactions" replace />} />
              <Route path="/withdraw" element={<Navigate to="/payments" replace />} />
              <Route path="/payment-methods" element={<Navigate to="/payments" replace />} />

              <Route path="/settings" element={<Settings />} />
              <Route path="/change-password" element={<ChangePassword />} />
              <Route path="/team" element={<Team />} />
              <Route path="/contact" element={<Contact />} />
            </Route>

            {/* Protected admin routes */}
            <Route element={<AdminRoute><AppLayout /></AdminRoute>}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/payments" element={<AdminPayments />} />
              <Route path="/admin/deposits" element={<AdminDeposits />} />
              <Route path="/admin/withdrawals" element={<AdminWithdrawals />} />
              <Route path="/admin/banners" element={<AdminBanners />} />
              <Route path="/admin/settings" element={<SystemSettingsPage />} />
            </Route>

            {/* Default route */}
            <Route path="/" element={<Navigate to={isAuthenticated ? (isAdmin() ? "/admin" : "/dashboard") : "/login"} />} />
          </Routes>
        </Suspense>
      </div>
    </Router>
  );
}

export default App;
